import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  TextField,
  IconButton,
  Box,
  Snackbar
} from "@mui/material";
import { collection, query, where, getDocs, setDoc, doc } from "firebase/firestore";
import { db } from "../../../firebase/firebaseConfig";

const PagosModal = ({ open, onClose, socio }) => {
  const [pagos, setPagos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newAnio, setNewAnio] = useState("");
  const [newMonto, setNewMonto] = useState("");
  const [updatingYear, setUpdatingYear] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  useEffect(() => {
    const fetchPagos = async () => {
      if (!socio) return;
      setLoading(true);
      try {
        const q = query(collection(db, "anualidades"), where("ID", "==", socio.ID.toString()));
        const querySnapshot = await getDocs(q);
        const data = querySnapshot.docs.map(doc => doc.data());
        
        //console.log("📄 Documentos obtenidos de Firebase:", data);
        setPagos(data.sort((a, b) => Number(b.ANIO) - Number(a.ANIO)));
      } catch (error) {
        console.error("Error al obtener pagos:", error);
      } finally {
        setLoading(false);
      }
    };

    if (open) fetchPagos();
  }, [open, socio]);

  const handleAddPago = async () => {
    if (!newAnio || !newMonto || !socio) return;
    setLoading(true);
    try {
      const newPago = {
        ID: socio.ID,
        ANIO: Number(newAnio),
        MONTO: newMonto
      };
      //console.log("Saving newPago:", newPago);
      //console.log("Saving newPago:", newPago);

      await setDoc(doc(db, "anualidades", `${socio.ID}_${newAnio}`), newPago);

      // Re-fetch payments instead of manually updating state
      const q = query(collection(db, "anualidades"), where("ID", "==", socio.ID.toString()));
      const querySnapshot = await getDocs(q);
      const data = querySnapshot.docs.map(doc => doc.data());

      setPagos(data.sort((a, b) => Number(b.ANIO) - Number(a.ANIO)));

      setNewAnio(""); 
      setNewMonto("");
      setSnackbarOpen(true);
    } catch (error) {
      console.error("Error al guardar el pago:", error);
    } finally {
      setLoading(false);
    }
  };

  if (!socio) return null;

  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
        <DialogTitle>Anualidades de {socio.NOMBRE_APELLIDO}</DialogTitle>
        <DialogContent dividers>
          <Box display="flex" gap={2} mb={2}>
            <TextField
              label="Año"
              type="number"
              value={newAnio}
              onChange={(e) => setNewAnio(e.target.value)}
            />
            <TextField
              label="Monto"
              type="text"
              value={newMonto}
              onChange={(e) => setNewMonto(e.target.value)}
            />
            <Button variant="contained" onClick={handleAddPago}>Agregar</Button>
          </Box>
          {loading ? (
            <CircularProgress />
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Año</TableCell>
                    <TableCell>Monto</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(() => {
                    const startYear = new Date(socio.FECHA_MEMBRESIA).getFullYear();
                    const pagosMap = Object.fromEntries(pagos.map(p => [Number(p.ANIO), p.MONTO]));
                    const rows = [];
                    const maxYear = Math.max(...pagos.map(p => Number(p.ANIO)), startYear);
                    for (let y = maxYear; y >= startYear; y--) {
                      rows.push(
                        <TableRow key={y}>
                          <TableCell>{y}</TableCell>
                          <TableCell>
                            <Box display="flex" gap={1} alignItems="center">
                              <TextField
                                size="small"
                                value={pagosMap[y] ?? "0"}
                                onChange={(e) => {
                                  const newPagos = [...pagos];
                                  const index = newPagos.findIndex(p => Number(p.ANIO) === y);
                                  if (index !== -1) {
                                    newPagos[index].MONTO = e.target.value;
                                  } else {
                                    newPagos.push({ ID: socio.ID, ANIO: y, MONTO: e.target.value });
                                  }
                                  setPagos(newPagos);
                                }}
                              />
                              <Button
                                variant="outlined"
                                size="small"
                                disabled={updatingYear === y}
                                onClick={async () => {
                                  try {
                                    setUpdatingYear(y);
                                    const monto = pagosMap[y] ?? "0";
                                    const found = pagos.find(p => Number(p.ANIO) === y);
                                    const value = found?.MONTO ?? monto;
                                    const updatedPago = { ID: socio.ID, ANIO: y, MONTO: value };
                                    await setDoc(doc(db, "anualidades", `${socio.ID}_${y}`), updatedPago);
                                    setSnackbarOpen(true);
                                  } catch (error) {
                                    console.error("Error al actualizar el pago:", error);
                                  } finally {
                                    setUpdatingYear(null);
                                  }
                                }}
                              >
                                {updatingYear === y ? <CircularProgress size={18} /> : "Actualizar"}
                              </Button>
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    }
                    return rows;
                  })()}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cerrar</Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        message="Pago actualizado correctamente"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </>
  );
};

export default PagosModal;